import { useCallback } from 'react';
import { useOptionalTenant } from '../contexts/TenantContext';
import { useMyPermissions } from './usePermission';
import { useUserStore } from '../store/userStore';

/**
 * Разделы компании — как на бэке (`backend/src/tenancy/modules.ts`). Включает их платформа
 * (`Tenant.modules`); пустой список — включены все. Дашборд, профиль, настройки, люди и
 * структура, роли, справка — всегда.
 */
export const COMPANY_MODULES = [
  'competencyMap',
  'goals',
  'essays',
  'quarterly',
  'performanceReview',
  'review360',
  'calendar',
  'learning',
] as const;

export type CompanyModule = (typeof COMPANY_MODULES)[number];

export const isModuleOn = (modules: readonly string[] | undefined, key: CompanyModule) =>
  !modules?.length || modules.includes(key);

/**
 * Разделы компании из `/tenant/branding`, а у вошедшего — ещё и его разделы из
 * `/users/me/permissions` (раздел может быть сужен до ролей). Пока они не пришли
 * (`isLoaded: false`) — не включён ни один: меню, дашборд и профиль не мигают разделами, которых
 * нет. Без провайдера (тесты компонентов) — включены все.
 */
export function useCompanyModules() {
  const tenant = useOptionalTenant();
  const userId = useUserStore((s) => s.user?._id);
  const { data: perms, isPending: isPermsPending, isError } = useMyPermissions();
  const modules = tenant?.branding.modules;
  const isLoaded = (tenant?.isLoaded ?? true) && (!userId || !isPermsPending || isError);
  // Стабильная функция: её кладут в зависимости useMemo (список дел на главной).
  const key = (modules ?? []).join(',');
  const mine = perms?.modules?.join(',');
  const hasModule = useCallback(
    (module: CompanyModule) =>
      isLoaded &&
      isModuleOn(key ? key.split(',') : undefined, module) &&
      // Права не загрузились — разделы закрыты: сервер всё равно ответит 404 на суженный.
      (mine === undefined ? !isError : mine.split(',').includes(module)),
    [key, mine, isLoaded, isError],
  );
  // Подключён ли раздел компании, без личного доступа: настройки компании (сроки, пометки людей)
  // нужны и админу, который сам разделом не пользуется.
  const hasCompanyModule = useCallback(
    (module: CompanyModule) =>
      (tenant?.isLoaded ?? true) && isModuleOn(key ? key.split(',') : undefined, module),
    [key, tenant?.isLoaded],
  );
  return {
    hasCompanyModule,
    /** Список задан платформой; пусто — все разделы. */
    modules: modules ?? [],
    isLoaded,
    hasModule,
  };
}
